import { fairValueBadge, bestValueBadge } from "../../components/midpoint/mobility/vehicleMarketFormat";

import { SEARCH_BUDGET_OPTION_DEFS } from "../../utils/vehicleSearch/vehicleSearchPriceOptions.js";

export const BUDGET_OPTIONS = SEARCH_BUDGET_OPTION_DEFS;

export const ALL_CITIES_VALUE = "__all_cities__";

export const DEFAULT_CITY_OPTION = "Todas las ciudades";

export const CITY_OPTIONS = [DEFAULT_CITY_OPTION, "Medellín", "Bogotá", "Cali", "Barranquilla"];

/**
 * @param {number|null|undefined} cop
 * @param {string} [currency]
 * @param {{ formatMoney?: Function, unavailableLabel?: string }} [options]
 */
export function formatTemplatePrice(cop, currency = "COP", options = {}) {
  const { formatMoney, unavailableLabel = "Precio no disponible" } = options;
  if (cop == null || Number.isNaN(Number(cop))) {
    return unavailableLabel;
  }
  if (typeof formatMoney === "function") {
    return formatMoney(Number(cop), { displayCurrency: currency });
  }
  const formatted = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(Number(cop));
  return `COP $${formatted}`;
}

export function formatCompactMileage(km) {
  if (km == null) return "Kilometraje no disponible";
  if (km >= 1000) {
    return `${Math.round(km / 1000)}k`;
  }
  return String(km);
}

export function formatTransmissionShort(value) {
  if (!value) return "—";
  const normalized = String(value).toLowerCase();
  if (normalized === "unknown") return "—";
  if (normalized.includes("auto")) return "Auto";
  if (normalized.includes("mec") || normalized.includes("manual")) return "Mec.";
  return value;
}

export function formatFreshLabel(listing) {
  const seenAt = listing.lastConfirmedAt || listing.lastSeenAt || listing.lastObservedAt;
  if (!seenAt) return "Disponible hoy";
  const hours = (Date.now() - new Date(seenAt).getTime()) / 3_600_000;
  if (hours < 1) return "Confirmado ahora";
  if (hours < 24) return `Confirmado ${Math.max(1, Math.round(hours))}h`;
  if (hours < 48) return "Disponible hoy";
  return "Disponible";
}

export function formatListingScore(listing) {
  const score = listing.bestValueScore ?? listing.dataQualityScore;
  if (score == null) return null;
  return `${Math.round(Number(score))}/100`;
}

export function getTemplateBadges(listing) {
  const badges = [];
  const fair = fairValueBadge(listing);
  if (fair?.label) {
    const labelMap = {
      "Great Deal": "Precio justo",
      "Great price": "Precio justo",
      "Good Deal": "Buen precio",
      "Fair Price": "Precio justo",
      "Fair price": "Precio justo",
      "High Price": "Sobre mercado",
      Overpriced: "Sobre mercado",
    };
    badges.push({
      text: labelMap[fair.label] || fair.label,
      className: "green",
    });
  }

  const seller = String(listing.sellerType || "").toUpperCase();
  if (listing.lastPriceChangeType === "PRICE_DECREASE") {
    badges.push({ text: "Bajó de precio", className: "amber" });
  } else if (seller !== "DEALER" && seller !== "BRAND_DEALER") {
    const best = bestValueBadge(listing);
    if (best?.label === "Excellent Value") {
      badges.push({ text: "Alta demanda", className: "amber" });
    } else if (listing.mileageKm != null && listing.mileageKm < 90000) {
      badges.push({ text: "Buen kilometraje", className: "green" });
    } else {
      badges.push({ text: "Disponible", className: "blue" });
    }
  }

  return badges.slice(0, 2);
}

export function buildVehicleSearchApiParams(
  {
    search,
    city,
    budgetKey,
    bodyType,
    fuelType,
    allCitiesValue = ALL_CITIES_VALUE,
    budgetRanges = BUDGET_OPTIONS,
  },
  base = {}
) {
  const params = { ...base };
  if (search?.trim()) {
    params.keyword = search.trim();
  }
  if (city && city !== allCitiesValue && city !== DEFAULT_CITY_OPTION) {
    params.city = city;
  }
  const budget = budgetRanges.find(
    (option) => option.value === budgetKey || option.label === budgetKey
  );
  if (budget?.priceMin != null) {
    params.priceMinCop = budget.priceMin;
  }
  if (budget?.priceMax != null) {
    params.priceMaxCop = budget.priceMax;
  }
  if (bodyType?.trim()) {
    params.bodyType = bodyType.trim();
  }
  if (fuelType?.trim()) {
    params.fuelType = fuelType.trim();
  }
  return params;
}

export function buildVehicleSearchParams(options) {
  const params = new URLSearchParams();
  const apiParams = buildVehicleSearchApiParams(options);
  Object.entries(apiParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  });
  return params;
}

export function getAutoclaroHomeNavOffset(navEl) {
  if (!navEl) return 0;
  return Math.ceil(navEl.getBoundingClientRect().height);
}

export function scrollAutoclaroHomeToAnchor(hash, options = {}) {
  const { navSelector = ".autoclaro-home .nav", behavior = "smooth" } = options;
  if (!hash || hash === "#") return false;

  const target = document.querySelector(hash);
  if (!target) return false;

  const nav = document.querySelector(navSelector);
  const offset = getAutoclaroHomeNavOffset(nav);
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), behavior });
  return true;
}

export function bindAutoclaroHomeAnchorScroll(rootSelector = ".autoclaro-home") {
  const root = document.querySelector(rootSelector);
  if (!root) return () => {};

  const nav = root.querySelector(".nav");

  const syncNavOffset = () => {
    root.style.setProperty("--autoclaro-nav-offset", `${getAutoclaroHomeNavOffset(nav)}px`);
  };

  syncNavOffset();

  const resizeObserver =
    typeof ResizeObserver !== "undefined" && nav ? new ResizeObserver(syncNavOffset) : null;
  resizeObserver?.observe(nav);
  window.addEventListener("resize", syncNavOffset);

  const scrollToHash = (hash, behavior = "smooth") => {
    scrollAutoclaroHomeToAnchor(hash, { behavior });
  };

  const handleClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !root.contains(link)) return;

    const hash = link.getAttribute("href");
    if (!hash || hash === "#") return;

    const target = document.querySelector(hash);
    if (!target) return;

    event.preventDefault();
    scrollToHash(hash);
    if (window.history?.pushState) {
      window.history.pushState(null, "", hash);
    } else {
      window.location.hash = hash.slice(1);
    }
  };

  root.addEventListener("click", handleClick);

  const handleHashChange = () => {
    if (window.location.hash) {
      scrollToHash(window.location.hash, "auto");
    }
  };

  requestAnimationFrame(handleHashChange);
  window.addEventListener("hashchange", handleHashChange);

  return () => {
    root.removeEventListener("click", handleClick);
    window.removeEventListener("hashchange", handleHashChange);
    window.removeEventListener("resize", syncNavOffset);
    resizeObserver?.disconnect();
  };
}
